import AES from 'crypto-js/aes';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';

import type { CartListsItem } from '@/api/types/cart';
import type {
  ProductsListCorrelationImage,
  ProductsListCorrelationItem,
  ProductsListItem,
  ProductsListResponse,
} from '@/api/types/product';
import { loadScript } from '@/common/script-loader';
/** 设备/环境检测结果 */
export interface DeviceType {
  isMoble?: boolean;
  isIOS?: boolean;
  isMac?: boolean;
  isAndroid?: boolean;
  isIphone?: boolean;
  isIpad?: boolean;
  isChrome?: boolean;
  isSafari?: boolean;
  isFirefox?: boolean;
  isOpera?: boolean;
  isUc?: boolean;
  is360?: boolean;
  isBaidu?: boolean;
  isSougou?: boolean;
  isLB?: boolean;
  isWX?: boolean;
  isQQ?: boolean;
  isIE6?: boolean;
  isIE7?: boolean;
  isIE8?: boolean;
  isIE9?: boolean;
  isIE10?: boolean;
  isIE11?: boolean;
  isWindowsCe?: boolean;
  isWindowsMobile?: boolean;
  isWin2K?: boolean;
  isXP?: boolean;
  isVista?: boolean;
  isWin7?: boolean;
  isWin8?: boolean;
  isWin81?: boolean;
  isWin10?: boolean;
  isChromeOnAndroid?: boolean;
}

/** 日期格式占位符映射表 */
interface DateFormatMap {
  'M+': number;
  'D+': number;
  'h+': number;
  'H+': number;
  'm+': number;
  's+': number;
  'q+': number;
  S: number;
}

/** 星期中文映射 */
interface WeekMap {
  [key: string]: string;
}

const { hasOwnProperty } = Object.prototype;

/**
 * 图片压缩：将 base64 图片按最大边宽压缩后转为 JPEG
 * @param base64 - base64 图片字符串
 * @param w - 最大宽度（像素），超过则按比例缩放
 * @param callback - 压缩完成回调，参数为压缩后的 base64 字符串
 */
export function imgCompression(
  base64: string,
  w: number,
  callback: (dataUrl: string) => void,
): void {
  const newImage = new Image();
  newImage.setAttribute('crossOrigin', 'Anonymous');
  newImage.src = base64;
  let imgWidth: number;
  let imgHeight: number;
  newImage.onload = () => {
    imgWidth = newImage.width;
    imgHeight = newImage.height;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (Math.max(imgWidth, imgHeight) > w) {
      canvas.width = imgWidth > imgHeight ? w * 1.5 : ((w * imgWidth) / imgHeight) * 1.5;
      canvas.height = imgWidth > imgHeight ? ((w * imgHeight) / imgWidth) * 1.5 : w * 1.5;
    } else {
      canvas.width = imgWidth;
      canvas.height = imgHeight;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(newImage, 0, 0, canvas.width, canvas.height);
    callback(canvas.toDataURL('image/jpeg', 0.8));
  };
}

/**
 * 生成游客标识：16 位随机字符串（数字与字母混合）
 * @returns 16 位游客 GUID
 */
export function visitor(): string {
  let guid = '';
  for (let i = 1; i <= 16; i++) {
    const flag = Math.floor(Math.random() * 10);
    if (flag % 2 === 0) {
      const n = Math.floor(flag * 2.4) + 65;
      guid += String.fromCharCode(n);
    } else {
      guid += flag;
    }
  }
  return guid;
}

/**
 * 时间格式化：将 Date/时间戳/日期字符串格式化为指定格式
 * @param date - 日期（Date、时间戳或日期字符串）
 * @param fmt - 格式字符串，默认 'YYYY-MM-DD HH:mm:ss'，支持 Y/M/D/H/h/m/s/q/S/E
 * @returns 格式化后的日期字符串
 */
export function dateFormat(
  date: Date | number | string | null | undefined,
  fmt: string = 'YYYY-MM-DD HH:mm:ss',
): string {
  if (!date) {
    return '';
  }
  let d: Date;
  if (typeof date === 'string') {
    d = new Date(date.replace(/-/g, '/'));
  } else if (typeof date === 'number') {
    d = new Date(date);
  } else {
    d = date;
  }
  const o: DateFormatMap = {
    'M+': d.getMonth() + 1,
    'D+': d.getDate(),
    'h+': d.getHours() % 12 === 0 ? 12 : d.getHours() % 12,
    'H+': d.getHours(),
    'm+': d.getMinutes(),
    's+': d.getSeconds(),
    'q+': Math.floor((d.getMonth() + 3) / 3),
    S: d.getMilliseconds(),
  };
  const week: WeekMap = {
    0: '\u65e5',
    1: '\u4e00',
    2: '\u4e8c',
    3: '\u4e09',
    4: '\u56db',
    5: '\u4e94',
    6: '\u516d',
  };
  if (/(Y+)/.test(fmt)) {
    fmt = fmt.replace(RegExp.$1, `${d.getFullYear()}`.substr(4 - RegExp.$1.length));
  }
  if (/(E+)/.test(fmt)) {
    fmt = fmt.replace(
      RegExp.$1,
      (RegExp.$1.length > 1 ? (RegExp.$1.length > 2 ? '\u661f\u671f' : '\u5468') : '') +
        week[`${d.getDay()}`],
    );
  }
  for (const k in o) {
    if (new RegExp(`(${k})`).test(fmt)) {
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length === 1
          ? String(o[k as keyof DateFormatMap])
          : `00${o[k as keyof DateFormatMap]}`.substr(`${o[k as keyof DateFormatMap]}`.length),
      );
    }
  }
  return fmt;
}

/**
 * 数字千分位补位：按指定长度分组并插入分隔符
 * @param source - 数字或字符串
 * @param length - 每组位数，默认 3
 * @returns 补位后的字符串，如 "1,234.56"
 */
export function numberFormat(source: number | string, length: number = 3): string {
  const parts = String(source).split('.');
  parts[0] = parts[0].replace(new RegExp(`(\\d)(?=(\\d{${length}})+$)`, 'ig'), '$1,');
  return parts.join('.');
}

/**
 * 生成 [min, max] 范围内的随机整数
 * @param min - 最小值
 * @param max - 最大值
 * @returns 随机整数
 */
export function numberRandom(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

/**
 * 解析 URL 查询字符串为键值对象
 * @param url - 含 query 的 URL 或 search 字符串
 * @returns 参数对象，如 { a: '1', b: '2' }
 */
export function getSearch(url: string): Record<string, string> {
  const obj: Record<string, string> = {};
  url.replace(/([^?&=]+)=([^&#]+)/g, (_: string, k: string, v: string) => {
    obj[k] = v;
    return `${k}=${v}`;
  });
  return obj;
}

/**
 * 为 URL 添加查询参数（会覆盖同名字段）
 * @param url - 原始 URL
 * @param object - 要添加的键值对（null/undefined 不写入，0 和 false 会写入）
 * @returns 带新参数的完整 URL
 */
export function addSearchParams(url: string, object: Record<string, unknown>): string {
  const newUrl = new URL(url);
  for (const key in object) {
    if (!(!object[key] && object[key] !== 0 && object[key] !== false)) {
      newUrl.searchParams.set(key, encodeURIComponent(String(object[key])));
    }
  }
  return newUrl.toString();
}

/**
 * 高精度加法（避免浮点误差）
 * @param arg1 - 加数一
 * @param arg2 - 加数二
 * @returns 和
 */
export function add(arg1: number | string, arg2: number | string): number {
  let r1: number;
  let r2: number;
  try {
    r1 = arg1.toString().split('.')[1].length;
  } catch {
    r1 = 0;
  }
  try {
    r2 = arg2.toString().split('.')[1].length;
  } catch {
    r2 = 0;
  }
  /* 这里在原有的小数位上+1，可以避免一些问题。比如 add(268.34, 0.83) 会出问题。原因在于 268.33*100 会出现不精确的结果 */
  const m = Math.pow(10, Math.max(r1 + 1, r2 + 1));
  return (Number(arg1) * m + Number(arg2) * m) / m;
}

/**
 * 高精度减法（避免浮点误差）
 * @param arg1 - 被减数
 * @param arg2 - 减数
 * @returns 差（字符串形式，保留小数位）
 */
export function accSub(arg1: number, arg2: number): string {
  let r1: number;
  let r2: number;
  try {
    r1 = arg1.toString().split('.')[1].length;
  } catch {
    r1 = 0;
  }
  try {
    r2 = arg2.toString().split('.')[1].length;
  } catch {
    r2 = 0;
  }
  /* 这里在原有的小数位上+1，可以避免一些问题。比如 accSub(268.34, 0.83) 会出问题。原因在于 268.33*100 会出现不精确的结果 */
  const m = Math.pow(10, Math.max(r1 + 1, r2 + 1));
  const n = r1 >= r2 ? r1 : r2;
  return ((arg1 * m - arg2 * m) / m).toFixed(n);
}

/**
 * 高精度乘法（避免浮点误差）
 * @param arg1 - 乘数一
 * @param arg2 - 乘数二
 * @returns 积
 */
export function accMul(arg1: number, arg2: number): number {
  let m = 0;
  const s1 = arg1.toString();
  const s2 = arg2.toString();
  try {
    const sub = s1.split('.')[1];
    if (sub && sub.length) {
      m += sub.length;
    }
  } catch (e) {
    console.log(e);
  }
  try {
    const sub = s2.split('.')[1];
    if (sub && sub.length) {
      m += sub.length;
    }
  } catch (e) {
    console.log(e);
  }
  return (Number(s1.replace('.', '')) * Number(s2.replace('.', ''))) / Math.pow(10, m);
}

/**
 * 高精度除法（避免浮点误差）
 * @param arg1 - 被除数
 * @param arg2 - 除数
 * @returns 商
 */
export function accDiv(arg1: number, arg2: number): number {
  let t1 = 0;
  let t2 = 0;
  try {
    const sub = arg1.toString().split('.')[1];
    if (sub && sub.length) {
      t1 = sub.length;
    }
  } catch (e) {
    console.log(e);
  }
  try {
    const sub = arg2.toString().split('.')[1];
    if (sub && sub.length) {
      t2 = sub.length;
    }
  } catch (e) {
    console.log(e);
  }
  return (
    (Number(arg1.toString().replace('.', '')) / Number(arg2.toString().replace('.', ''))) *
    Math.pow(10, t2 - t1)
  );
}

/**
 * 获取当前设备/环境类型（UA 检测）
 * @returns 设备类型对象；服务端渲染时返回空对象
 */
export function getDeviceType(): DeviceType | Record<string, never> {
  if (process.server) {
    return {};
  }
  const UserAgent = window.navigator.userAgent.toLowerCase();
  let isChromeOnAndroid = false;
  if (/android/i.test(UserAgent)) {
    if (/chrome/i.test(UserAgent)) {
      const parts = UserAgent.split('chrome/');
      const fullVersionString = parts[1].split(' ')[0];
      const versionString = fullVersionString.split('.')[0];
      const version = parseInt(versionString, 10);
      if (version >= 27) {
        isChromeOnAndroid = true;
      }
    }
  }
  return {
    isMoble: /iphone|ipod|android.*mobile|windows.*phone|blackberry.*mobile/i.test(UserAgent), // 判断是否为移动端
    isIOS: /iphone|ipod|ipad|Macintosh/i.test(UserAgent), // 是否为苹果设备
    isMac: /Macintosh/i.test(UserAgent), // 是否为Mac os
    isAndroid: /android/i.test(UserAgent), // 是否为安卓移动端
    isIphone: /iphone os/.test(UserAgent), // 是否为iphone
    isIpad: /ipad/.test(UserAgent), // 是否为ipad
    isChrome: /chrome/.test(UserAgent.substr(-33, 6)), // Chrome浏览器
    isSafari: /safari/.test(UserAgent) && !/chrome/.test(UserAgent), // safari浏览器
    isFirefox: /firefox/.test(UserAgent), // 火狐浏览器
    isOpera: /opera/.test(UserAgent), // Opera浏览器
    isUc: /ucweb/.test(UserAgent), // UC浏览器
    is360: /360se/.test(UserAgent), // 360浏览器
    isBaidu: /bidubrowser/.test(UserAgent), // 百度浏览器
    isSougou: /metasr/.test(UserAgent), // 搜狗浏览器
    isLB: /lbbrowser/.test(UserAgent), // 猎豹浏览器
    isWX: /micromessenger/.test(UserAgent), // 微信内置浏览器
    isQQ: /qqbrowser/.test(UserAgent), // QQ浏览器
    isIE6: /msie 6.0/.test(UserAgent), // IE6
    isIE7: /msie 7.0/.test(UserAgent), // IE7
    isIE8: /msie 8.0/.test(UserAgent), // IE8
    isIE9: /msie 9.0/.test(UserAgent), // IE9
    isIE10: /msie 10.0/.test(UserAgent), // IE10
    isIE11: /msie 11.0/.test(UserAgent), // IE11
    isWindowsCe: /windows ce/.test(UserAgent),
    isWindowsMobile: /windows mobile/.test(UserAgent),
    isWin2K: /windows nt 5.0/.test(UserAgent),
    isXP: /windows nt 5.1/.test(UserAgent),
    isVista: /windows nt 6.0/.test(UserAgent),
    isWin7: /windows nt 6.1/.test(UserAgent),
    isWin8: /windows nt 6.2/.test(UserAgent),
    isWin81: /windows nt 6.3/.test(UserAgent),
    isWin10: /windows nt 10.0/.test(UserAgent),
    isChromeOnAndroid,
  };
}

/**
 * 判断是否为函数
 * @param value - 待检测值
 * @returns 是否为函数
 */
export function isFunction(value: unknown): value is (...args: unknown[]) => unknown {
  return Object.prototype.toString.call(value) === '[object Function]';
}

/**
 * 判断是否为有效的“类数组长度”（非负整数且不超过安全整数）
 * @param value - 待检测值
 * @returns 是否为有效长度
 */
export function isLength(value: unknown): value is number {
  return (
    typeof value === 'number' && value > -1 && value % 1 === 0 && value <= Number.MAX_SAFE_INTEGER
  );
}

/**
 * 判断是否为类数组（有 length 且非函数）
 * @param value - 待检测值
 * @returns 是否为类数组
 */
export function isArrayLike(value: unknown): value is { length: number } {
  return value != null && isLength((value as { length: number }).length) && !isFunction(value);
}

/**
 * 判断是否为纯对象（Object 字面量/实例，非 Array/Date 等）
 * @param obj - 待检测值
 * @returns 是否为纯对象
 */
export function isPlainObject(obj: unknown): obj is Record<string, unknown> {
  return Object.prototype.toString.call(obj) === '[object Object]';
}

/**
 * 非空判断：null/undefined、空数组、空对象（无自有属性）视为空
 * @param value - 待检测值
 * @returns 是否为空
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (isArrayLike(value)) {
    return !(value as { length: number }).length;
  }
  if (isPlainObject(value)) {
    for (const key in value) {
      if (hasOwnProperty.call(value, key)) {
        return false;
      }
    }
  }
  return false;
}

/**
 * 判断是否为空对象：null/undefined 或无自有键的对象
 * @param obj - 待检测值
 * @returns 是否为空对象
 */
export function isEmptyObj(obj: unknown): boolean {
  if (obj == null) {
    return true;
  }
  return typeof obj === 'object' && obj !== null && Object.keys(obj).length === 0;
}
/**
 * 根据 cookie 名称获取 cookie 值
 * @param name - cookie 名称
 * @returns 对应的 cookie 值，未找到返回空字符串
 */
export function getCookieValue(name: string): string {
  const cookieList = document.cookie.split('; ');
  let cookieValue = '';
  cookieList.find((item) => {
    const itemInfor = item.split('=');
    if (itemInfor[0] === name) {
      cookieValue = itemInfor[1];
      return true;
    }
    return false;
  });
  return cookieValue;
}

/**
 * 从当前页 URL 的 search 中按名称获取参数值
 * @param name - 参数名
 * @returns 参数值，未找到返回空字符串；服务端渲染时返回空字符串
 */
export function getQueryByName(name: string): string {
  if (process.server) return '';
  const url = location.search;
  const reg = new RegExp(`[?&]${name}=([^&#]+)`);
  const query = url.match(reg);
  return query ? query[1] : '';
}
// 判断是否是在安卓版Facebook APP 的内置浏览器
// export function isAndrFacebookApp() {
//     const u = navigator.userAgent
//     return /FBAN|FBAV/i.test(u) && /Android/i.test(u)
// }
/** 带过期时间的 localStorage 读写封装 */
export const getSetLocalStorage = {
  /** 写入键值，并设置过期时间（毫秒时间戳） */
  set(key: string, value: unknown, ttl_ms: number): void {
    const data = { value, expired: new Date(ttl_ms).getTime() };
    localStorage.setItem(key, JSON.stringify(data));
  },
  /** 读取键值，若已过期则删除并返回 null */
  get(key: string): unknown {
    const raw = localStorage.getItem(key);
    const data = raw ? (JSON.parse(raw) as { value: unknown; expired?: number }) : null;
    if (data !== null) {
      if (data.expired != null && data.expired < new Date().getTime()) {
        localStorage.removeItem(key);
      } else {
        return data.value;
      }
    }
    return null;
  },
  /** 删除指定键 */
  remove(key: string): void {
    localStorage.removeItem(key);
  },
};

/** 带过期时间的 sessionStorage 读写封装 */
export const getSetSessionStorage = {
  /** 写入键值，并设置过期时间（毫秒时间戳） */
  set(key: string, value: unknown, ttl_ms: number): void {
    const data = { value, expired: new Date(ttl_ms).getTime() };
    sessionStorage.setItem(key, JSON.stringify(data));
  },
  /** 读取键值，若已过期则删除并返回 null */
  get(key: string): unknown {
    const raw = sessionStorage.getItem(key);
    const data = raw ? (JSON.parse(raw) as { value: unknown; expired?: number }) : null;
    if (data !== null) {
      if (data.expired != null && data.expired < new Date().getTime()) {
        sessionStorage.removeItem(key);
      } else {
        return data.value;
      }
    }
    return null;
  },
  /** 删除指定键 */
  remove(key: string): void {
    sessionStorage.removeItem(key);
  },
};
/** Afterpay 展示条件：币种与支持国家映射（金额要求 <= 2000） */
export const afterpayEssentialMap: Record<string, string> = {
  AUD: 'AU',
  CAD: 'CA',
  USD: 'US',
};
/* klarna展示要求的地址国家和其他对应关系（shipping地址和billing地址国家要求一致） */
// export const klarnaEssentialMap = {
//     US: {
//         CURRENCY: 'USD', // 币种
//         MIN: 10, // 最小值
//         MAX: 1000, // 最大值
//         CYCLE: 4, // 分期期数
//         TERM_LINK: 'https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_us/sliceitinx', // 分期协议链接
//         CAN_REDUCE: true, // 是否支持klarna优惠
//     },
//     GB: {
//         CURRENCY: 'GBP',
//         MIN: 30,
//         MAX: 1000,
//         CYCLE: 3,
//         TERM_LINK: 'https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_gb/paylaterin3',
//         CAN_REDUCE: false,
//     },
//     IT: {
//         CURRENCY: 'EUR',
//         MIN: 35,
//         MAX: 1000,
//         CYCLE: 3,
//         TERM_LINK: 'https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_it/paylaterin3',
//         CAN_REDUCE: false,
//     },
//     FR: {
//         CURRENCY: 'EUR',
//         MIN: 35,
//         MAX: 1000,
//         CYCLE: 3,
//         TERM_LINK: 'https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_fr/paylaterin3',
//         CAN_REDUCE: false,
//     },
//     DE: { // 德国未开通分期支付，采用30天内付款（pay_later）
//         CURRENCY: 'EUR',
//         MIN: 0,
//         MAX: 3000,
//         CYCLE: 0, // 0表示不支持分期，采用pay_later付款
//         TERM_LINK: 'https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_de/paylaterin3',
//         CAN_REDUCE: false,
//     },
// }
/**
 * 防抖：在 delay 内多次调用只执行最后一次
 * @param fn - 要防抖的函数
 * @param delay - 延迟毫秒数，默认 300
 * @returns 防抖后的函数
 */

export function _debounce<T extends (...args: unknown[]) => unknown>(fn: T, delay?: number) {
  const d = delay ?? 300;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // 实测， (this, ...args) 这种写法没问题
  return function (this: unknown, ...args: Parameters<T>) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, d);
  };
}

/**
 * 监听谷歌翻译插件语言选择变化
 * 当选择英语时，自动删除翻译cookie并刷新页面
 */
export function observeGoogleTranslate(): void {
  window.transIframeTimer = setInterval(() => {
    if (window.transSelect) {
      clearInterval(window.transIframeTimer);
      // 删除不需要的文本显示
      const transSelectPar = document.querySelector('.skiptranslate.goog-te-gadget');
      if (transSelectPar) {
        const { childNodes } = transSelectPar;
        childNodes?.forEach((childNode) => {
          if (childNode.nodeName !== 'DIV') {
            transSelectPar.removeChild(childNode);
          }
        });
      }
      // 监听select点击事件
      window.transSelect.onchange = function () {
        if (!window.transSelect) return;
        const index = window.transSelect.selectedIndex;
        const lang = window.transSelect.options[index].value;
        if (lang === 'en') {
          deleteCookie('googtrans');
          window.location.reload();
        }
      };
    }
    window.transSelect = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
  }, 500);
}

/**
 * 删除指定名称的 cookie（含根路径及二级域名，以兼容翻译插件等）  toGMTString 改成 toUTCString  需要测试。AI回答说是不影响
 * @param name - cookie 名称
 */
export function deleteCookie(name: string): void {
  const Days = 30;
  const exp = new Date();
  exp.setTime(exp.getTime() - Days * 24 * 60 * 60 * 30);

  // 这里必定要注意，若是直接访问ip的话，不用注明域名domain
  // 但访问的是域名例如www.baidu.com时，翻译插件的cookie同时存在于一级和二级域名中
  // 即删除翻译cookie时要把domain=www.baidu.com和domain=.baidu.com两个cookie一块儿删除才行
  let { domain } = document;
  let domainIsIp = false;
  const dd = domain.split('.');
  if (dd.length === 4) {
    domainIsIp = true;
  }
  document.cookie = `${name}='';path=/;expires=${exp.toUTCString()}`;
  if (domainIsIp === false) {
    domain = `.${dd[1]}.${dd[2]}`;
    document.cookie = `${name}='';domain=${domain};expires=${exp.toUTCString()};path=/`;
  }
}

/**
 * 预加载单张图片
 * @param url - 图片 URL
 * @returns Promise，成功 resolve，失败 reject
 */
export const preloadImg = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (process.server) {
      resolve();
      return;
    }
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => reject();
    image.src = url;
  });
};

/**
 * 预加载多张图片
 * @param imgs - 图片 URL 数组
 * @returns Promise.all 结果
 */
export const preloadImgs = (imgs: string[]): Promise<void[]> => {
  const promiseArr = imgs.map((img) => preloadImg(img));
  return Promise.all(promiseArr);
};

/** S3 上传地址的域名（图片 CDN） */
export const S3_DOMAIN = 'https://vooglam-us-3.s3.us-west-2.amazonaws.com';

/** 夹片项（含 qty） */
interface ClipItemLike {
  qty?: number | string;
}

/**
 * 计算夹片总数（为解决循环依赖放在此处，未放入 clip.js）
 * @param list - 夹片列表，每项含 qty
 * @returns 总数量
 */
export function getClipsNum(list: ClipItemLike[] | null | undefined): number {
  let num = 0;
  if (!list?.length) return 0;
  list.forEach((item) => {
    num += Number(item.qty);
  });
  return num;
}

/**
 * @Description: //计算购物车商品总数量
 * @param list - 购物车列表
 * @param containClips - 是否包含夹片
 * @returns 总数量
 */
export function getNum(list: CartListsItem[], containClips = false): number {
  let num = 0;
  try {
    list.forEach((item: CartListsItem) => {
      num += item.qty ?? 0;
      if (containClips) {
        num += getClipsNum(item?.clips);
      }
    });
  } catch (error) {
    console.log(error);
  }
  return num;
}

/**
 * 格式化 URL
 * @param params - 参数对象，包含 jump_type 和 Jump_target
 * @returns 格式化后的 URL
 */
export function formatUrl(params: { jump_type: number; Jump_target: string }): string {
  const { jump_type, Jump_target } = params;
  // jump_type 跳转类型 number: 1 商品分组，2销售sku，3 系统页面，4 指定链接，5弹框
  if (!jump_type || !Jump_target) {
    return '';
  }
  let url = '';
  switch (jump_type) {
    case 1:
      url = `/goods-list/${Jump_target}`;
      break;
    case 2:
      url = `/goods-detail/${Jump_target}`;
      break;
    case 3:
      url = Jump_target;
      break;
    case 4:
      url = Jump_target;
      break;
    case 5:
      url = '';
      break;
    default:
      url = '';
      break;
  }
  return url;
}

/**
 * 收藏数量格式化：大于等于 1000 时显示为 x.xK 并千分位
 * @param _num - 数字或可转数字的值
 * @returns 数字或格式化的字符串（如 "1,234.5K"）
 */
export function wishNumWithK(_num: number | string): number | string {
  const num = Number(_num);
  if (Number.isNaN(num)) return 0;
  if (num < 0) return 0;
  if (num < 1000) {
    return num;
  }
  if (num >= 1000) {
    let newStr = '';
    let count = 0;
    const numK = (num / 1000).toFixed(1);
    const strArr = String(numK).split('.');
    let integerStr = strArr[0];
    for (let i = integerStr.length - 1; i >= 0; i--) {
      if (count % 3 === 0 && count !== 0) {
        newStr = `${integerStr.charAt(i)},${newStr}`;
      } else {
        newStr = integerStr.charAt(i) + newStr;
      }
      count++;
    }
    integerStr = `${newStr}${strArr[1] === '0' ? '' : `.${strArr[1]}`}K`;
    return integerStr;
  }
  return num;
}

/**
 * 收藏数量格式化：四舍五入到 k 单位
 * - 低于 1000 时返回具体数量（如 857）
 * - 高于 1000 时返回 xk 或 x.xk（如 1.2k、2k）
 * @param _num - 数字或可转数字的值
 * @returns 数字或格式化的字符串
 */
export function wishRoundK(_num: number | string): number | string {
  const num = Number(_num);
  if (Number.isNaN(num)) return 0;
  if (num < 0) return 0;
  if (num < 1000) {
    return num;
  }
  const numInK = num / 1000;
  const rounded = Math.round(numInK * 10) / 10;
  if (rounded % 1 === 0) {
    return `${Math.floor(rounded)}k`;
  }
  return `${rounded.toFixed(1)}k`;
}

/**
 * @description: txt中的/n字符全部转换为<br>
 * @param txt - 文本
 * @returns 转换后的文本
 */
export function lineBreakSymToBr(txt: string): string {
  if (Object.prototype.toString.call(txt) !== '[object String]') {
    return '';
  }
  const vHtml = txt.replace(/\n/g, '<br>');
  return vHtml;
}
/**
 * 将 16 进制颜色转为 rgba 字符串
 * @param str - 16 进制颜色，如 #ffffff、#fff
 * @param opacity - 透明度，如 0.1
 * @returns rgba(255,255,255,0.1)，非法输入返回 undefined
 */
export function set16ToRgba(str: string, opacity: number): string | undefined {
  const reg = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  if (!reg.test(str)) {
    return undefined;
  }
  let newStr = str.toLowerCase().replace(/#/g, '');
  const len = newStr.length;
  if (len === 3) {
    let t = '';
    for (let i = 0; i < len; i++) {
      t += newStr.slice(i, i + 1).concat(newStr.slice(i, i + 1));
    }
    newStr = t;
  }
  const arr: number[] = [];
  for (let i = 0; i < 6; i += 2) {
    const s = newStr.slice(i, i + 2);
    arr.push(parseInt(`0x${s}`, 16));
  }
  return `rgba(${arr.join(',')},${opacity})`;
}
/** 社媒短链配置：路由路径与跳转 URL 映射 */
export interface MediaShortLinkItem {
  routerLink: string;
  toLink: string;
}

export const mediaShortLink: MediaShortLinkItem[] = [
  {
    routerLink: '/ins',
    toLink: 'https://www.vooglam.com/?utm_source=social&utm_medium=ins',
  },
  {
    routerLink: '/FBb',
    toLink: 'https://www.vooglam.com/?utm_source=social&utm_medium=FBb',
  },
  {
    routerLink: '/tiktok',
    toLink: 'https://www.vooglam.com/?utm_source=social&utm_medium=tiktok',
  },
  {
    routerLink: '/inslist',
    toLink: 'https://www.vooglam.com/goods-list/376?page=1&utm_source=social&utm_medium=ins',
  },
  {
    routerLink: '/fblist',
    toLink: 'https://www.vooglam.com/goods-list/377?page=1&utm_source=social&utm_medium=FBb',
  },
  {
    routerLink: '/fblink',
    toLink: 'https://www.vooglam.com/goods-list/377?page=1&utm_source=social&utm_medium=FBlink',
  },
  {
    routerLink: '/page',
    toLink: 'https://www.vooglam.com/goods-list/377?page=1&utm_source=social&utm_medium=FBpage',
  },
  {
    routerLink: '/group',
    toLink: 'https://www.vooglam.com/goods-list/377?page=1&utm_source=social&utm_medium=FBgroup',
  },
  {
    routerLink: '/ytb',
    toLink: 'https://www.vooglam.com/?utm_source=Influencer&utm_medium=ytb',
  },
  {
    routerLink: '/link',
    toLink: 'https://www.vooglam.com/?utm_source=social&utm_medium=FBlink',
  },
];

/** 是否在 APP 内嵌页（根据 UA 判断 nextmar-xms-app） */
function checkNextmarApp(): boolean {
  if (process.client) {
    return navigator.userAgent.includes('nextmar-xms-app');
  }
  return false;
}

export const inNextmarApp: boolean = checkNextmarApp();
/** 是否在 APP 内打开（与 inNextmarApp 同源判断） */
export const inAppH5: boolean = checkNextmarApp();

/**
 * 生成指定长度的随机字符串（用于 DMP 页面 uuid 等）
 * @param e - 长度，默认 32
 * @returns 随机字符串
 */
export function randomString(e?: number): string {
  const len = e ?? 32;
  const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
  const max = chars.length;
  let n = '';
  for (let i = 0; i < len; i++) n += chars.charAt(Math.floor(Math.random() * max));
  return n;
}

/**
 * Akamai 图片裁剪：在 URL 上追加 Resize 参数，返回指定宽度的图片地址
 * @param src - 图片原路径（字符串或其它类型，非字符串时原样返回）
 * @param width - 目标宽度，默认 465（pc 2 倍、wap 3 倍场景）
 * @returns 带 im 参数的 URL，或空字符串；文档见项目语雀
 */
export function akamai(src: string | unknown, width: number = 800): string {
  if (src) {
    const encode = encodeURIComponent(`Resize,width=${width}`);
    if (typeof src === 'string') {
      return src + (src.includes('?') ? `&im=${encode}` : `?im=${encode}`);
    }
    return src as string;
  }
  return '';
}

/**
 * 数字向下取整到指定小数位（默认 2 位），返回字符串
 * @param number - 待处理数字
 * @param length - 保留小数位数，默认 2
 * @returns 字符串形式的数字；非法输入返回空字符串
 */
export function floorNumber(number: number | string, length: number = 2): string {
  const num = Number(number);
  const len = Number(length);
  if (!(num > 0) || !(len >= 0)) return '';
  const fixed = Number(num.toFixed(len + 2));
  const multiplier = Math.pow(10, len);
  return (Math.floor(fixed * multiplier) / multiplier).toFixed(2);
}

/**
 * 生成全局唯一的回调方法名，用于 H5 与 App 交互；老版本（&lt;3.0.0）可返回默认值
 * @param defaultVal - 老版本使用的默认方法名
 * @returns 唯一方法名或 defaultVal
 */
export function randomFuncName(defaultVal?: string): string {
  const version = getQueryByName('version') || '';
  if (version && parseInt(version[0], 10) < 3 && defaultVal) {
    return defaultVal;
  }
  return `app${randomString()}`;
}

/**
 * 版本号比较（按段比较，如 1.2.3）
 * @param v1 - 版本一
 * @param v2 - 版本二
 * @returns -1 表示 v1<v2，1 表示 v1>v2，0 表示相等
 */
export function compareVersions(v1: string, v2: string): -1 | 0 | 1 {
  const v1Parts = v1.split('.').map(Number);
  const v2Parts = v2.split('.').map(Number);
  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const num1 = v1Parts[i] ?? 0;
    const num2 = v2Parts[i] ?? 0;
    if (num1 < num2) return -1;
    if (num1 > num2) return 1;
  }
  return 0;
}

/**
 * 判断是否为墨西哥税号（RFC）格式：13 位或 18 位
 * @param value - 待校验字符串
 * @returns 是否符合格式
 */
export function isMXCPF(value: string): boolean {
  const format1 =
    /^[A-Z][AEIOU][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[A-Z0-9]{2}[0-9A-Z]$/;
  // 格式2: 18位
  const format2 =
    /^[A-Z][AEIOU][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[HM][A-Z]{2}[B-DF-HJ-NP-TV-Z]{3}(?:[0-9]|[A-Z])[0-9]$/;
  return format1.test(value) || format2.test(value);
}

/** 转义正则中的特殊字符 */
function escapeRegExp(string: string): string {
  try {
    if (!string) return '';
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  } catch (error) {
    console.log(error);
    return '';
  }
}

/**
 * 处理商品长名称：去掉以短名称开头且后跟分隔符的前缀，返回剩余部分
 * @param shortName - 短名称
 * @param longName - 原始长名称
 * @returns 处理后的长名称
 */
export function processLongName(shortName: string, longName: string): string {
  try {
    const escapedShort = escapeRegExp(shortName);
    // 匹配以短名称开头，后跟各种分隔符的情况
    const pattern = `^${escapedShort}(?:\\s+-\\s+|\\s+-|-\\s+|-|\\s+|$)`;
    const regex = new RegExp(pattern);

    // 替换匹配部分并修剪结果
    const processed = longName.replace(regex, '').trim();
    return processed;
  } catch (error) {
    console.log(error);
    return '';
  }
}

/** AES 加解密工具（CBC + Pkcs7，Base64 输出做 URL 编码） */
export class CryptoUtils {
  static KEY = Utf8.parse('zlmasternxcac0rb');
  static IV = Utf8.parse('zlmasterc447exzu');

  /** 加密：明文 -> Base64 -> encodeURIComponent */
  encrypt(message: string): string {
    try {
      const encrypted = AES.encrypt(message, CryptoUtils.KEY, {
        iv: CryptoUtils.IV,
      });
      // 将加密后的数据转换为 Base64 编码的字符串
      const encryptedBase64 = encrypted.ciphertext.toString(Base64);
      return encodeURIComponent(encryptedBase64);
    } catch (error) {
      console.log(error);
      return '';
    }
  }

  /** 解密：decodeURIComponent -> Base64 解析 -> AES 解密 */
  decrypt(encryptedBase64UrlSafe: string): string {
    try {
      // 将 URL 安全的 Base64 编码转换回标准 Base64 编码
      const encryptedBase64 = decodeURIComponent(encryptedBase64UrlSafe);
      const encryptedWordArray = Base64.parse(encryptedBase64);
      // 使用 CipherParams 对象或类型断言来满足 TypeScript 类型要求
      const decrypted = AES.decrypt({ ciphertext: encryptedWordArray } as any, CryptoUtils.KEY, {
        iv: CryptoUtils.IV,
      });
      const decryptedMessage = decrypted.toString(Utf8);
      return decryptedMessage;
    } catch (error) {
      console.log(error);
      return '';
    }
  }
}

/** 优惠券简化描述：折扣/满减规则与展示文案 */
export interface CouponSimpleDescResult {
  isPercent: boolean;
  value: number;
  valueDisplay: string;
  thresholdValue: number;
  thresholdType: number;
  rulesText: string;
  isSiteWide: boolean;
  way: number;
  detailsType: string;
}

/** 优惠券原始结构（仅用到的字段） */
interface CouponLike {
  way?: number;
  discounts_details?: {
    discounts_value?: number;
    discounts_threshold?: number;
    discounts_threshold_type?: number;
  };
  full_discounts_details?: {
    discounts_value?: number;
    discounts_threshold?: number;
  };
}

/**
 * 从优惠券数据中提取简化描述（way 1/5 折扣，way 2 满减）
 * @param coupon - 优惠券对象
 * @returns 简化描述对象，含 isPercent、value、valueDisplay、rulesText 等
 */
export function couponSimpleDesc(coupon: CouponLike | null | undefined): CouponSimpleDescResult {
  const safeNumber = (val: unknown, fallback: number = 0): number => {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  };

  const way = safeNumber(coupon && coupon.way);

  // 折扣（百分比）路径
  if (way === 1 || way === 5) {
    const details = (coupon && coupon.discounts_details) || {};
    const percentValue = safeNumber(details.discounts_value);
    const threshold = safeNumber(details.discounts_threshold);
    const thresholdType = safeNumber(details.discounts_threshold_type, 1); // 1: 金额, 2: 件数

    // SITEWIDE 判定：
    // - 金额门槛：0.01 代表全场
    // - 件数门槛：1 件代表全场
    const isSiteWide =
      (thresholdType === 1 && Number(threshold) === 0.01) ||
      (thresholdType === 2 && Number(threshold) === 1);

    let rulesText = 'SITEWIDE';
    if (!isSiteWide) {
      if (thresholdType === 1) {
        rulesText = `Orders $${threshold}+`;
      } else if (thresholdType === 2) {
        rulesText = `${threshold}items & above`;
      }
    }

    return {
      isPercent: true,
      value: percentValue,
      // 组件可选择自定义排版（如在 % 后拼接 OFF），这里给出常用拼接
      valueDisplay: `${parseInt(String(percentValue), 10)}%`,
      thresholdValue: threshold,
      thresholdType,
      rulesText,
      isSiteWide,
      // 兼容字段，便于有需要时判断原分支
      way,
      detailsType: 'discounts_details',
    };
  }

  // 满减（金额）路径
  if (way === 2) {
    const details = (coupon && coupon.full_discounts_details) || {};
    const amountOff = safeNumber(details.discounts_value);
    const threshold = safeNumber(details.discounts_threshold);

    // 常用全场判定：门槛 - 优惠额 = 0.01（以分为单位比较避免浮点误差）或 门槛直接为 0.01
    const isSiteWide =
      safeNumber(threshold) * 100 - safeNumber(amountOff) * 100 === 1 || Number(threshold) === 0.01;

    const rulesText = isSiteWide ? 'SITEWIDE' : `Orders $${threshold}+`;

    return {
      isPercent: false,
      value: amountOff,
      valueDisplay: `$${amountOff}`,
      thresholdValue: threshold,
      thresholdType: 1,
      rulesText,
      isSiteWide,
      way,
      detailsType: 'full_discounts_details',
    };
  }

  // 兜底：未知类型
  return {
    isPercent: false,
    value: 0,
    valueDisplay: '',
    thresholdValue: 0,
    thresholdType: 1,
    rulesText: '',
    isSiteWide: false,
    way,
    detailsType: 'unknown',
  };
}

/**
 * 检测 URL 中 utm_medium=review，若存在则写入 sessionStorage 标识（24 小时过期）
 */
export function checkAndSaveUtmReviewFlag(): void {
  const utmMedium = getQueryByName('utm_medium') || '';
  if (utmMedium && utmMedium === 'review') {
    const existingFlag = getSetSessionStorage.get('voo-utm-review-flag');
    if (!existingFlag) {
      const now = Date.now();
      const expireTime = now + 24 * 60 * 60 * 1000;
      getSetSessionStorage.set('voo-utm-review-flag', true, expireTime);
    }
  }
}

/**
 * 过滤服务端商品列表接口数据，删除大字段以提升性能
 * @param res - 接口响应，会原地修改
 * @returns 原响应对象
 */
export const filterGoodsListData = (res: ProductsListResponse): ProductsListResponse => {
  if (res.code === 200 && res.data) {
    if (res.data.group) {
      delete res.data.group.wap_img;
      delete res.data.group.app_img;
      delete res.data.group.pc_img;
    }
    const delItemKeys = [
      'lens_type',
      'image',
      'product_group',
      'prescription_type',
      'tryon_android_url',
      'tryon_ios_zip',
      'is_frame_only',
      'is_frame_only_lens',
      'point_frame_only',
      'point_frame_only_lens',
    ];
    res.data.list = res.data.list?.map((item) => {
      try {
        for (let i = 0; i < delItemKeys.length; i++) {
          const key = delItemKeys[i];
          if (key === 'image') {
            const img = item[key];
            item[key] = {
              product: img?.product?.slice?.(0, 1) ?? [],
              material: img?.material?.slice?.(0, 1) ?? [],
            };
          } else {
            delete item[key as keyof typeof item];
          }
        }
      } catch (_error) {
        console.error(_error);
      }
      return item;
    });
  }
  return res;
};
// -------------以下内容还没有审核-------------
/** 商品列表滚动管理器的配置选项 */
export interface ProductListScrollManagerOptions {
  productSelector?: string;
  listOptionsSelector?: string;
  headerSelector?: string;
  visibilityThreshold?: number;
  extraScrollOffset?: number;
}

/**
 * 商品列表滚动位置管理器
 * 用于在切换单列/双列模式时保持当前视图中商品的位置
 */
export class ProductListScrollManager {
  productSelector: string;
  listOptionsSelector: string;
  headerSelector: string;
  visibilityThreshold: number;
  extraScrollOffset: number;

  constructor(options: ProductListScrollManagerOptions = {}) {
    this.productSelector = options.productSelector ?? '.product-cart-content-box';
    this.listOptionsSelector = options.listOptionsSelector ?? '#list-options';
    this.headerSelector = options.headerSelector ?? '#header';
    this.visibilityThreshold = options.visibilityThreshold ?? 0.5;
    this.extraScrollOffset =
      options.extraScrollOffset !== undefined ? options.extraScrollOffset : 14;
  }

  /** 计算页面滚动偏移位置（列表选项高度 + 头部高度） */
  getScrollOffset(): number {
    const listEl = document.querySelector(this.listOptionsSelector) as HTMLElement | null;
    const headerEl = document.querySelector(this.headerSelector) as HTMLElement | null;
    return (listEl?.offsetHeight ?? 0) + (headerEl?.offsetHeight ?? 0);
  }

  /** 获取当前视口中第一个可见的商品元素（按可见比例阈值） */
  getFirstVisibleProductElement(): HTMLElement | null {
    const headerHeight = this.getScrollOffset();
    const elements = document.querySelectorAll(this.productSelector);

    for (const element of elements) {
      const el = element as HTMLElement;
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementHeight = rect.bottom - rect.top;
      if (elementHeight <= 0) continue;
      const visibleTop = Math.max(rect.top, headerHeight);
      const visibleBottom = Math.min(rect.bottom, windowHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = visibleHeight / elementHeight;
      if (visibilityRatio >= this.visibilityThreshold) {
        return el;
      }
    }
    return document.querySelector(this.productSelector) as HTMLElement | null;
  }

  /** 获取列表选项区域高度（用于滚动补偿） */
  getListOptionsHeight(): number {
    const el = document.querySelector(this.listOptionsSelector) as HTMLElement | null;
    return el?.offsetHeight ?? 0;
  }

  /** 获取滚动补偿偏移量（列表选项高度 + 单/双列额外偏移） */
  getScrollCompensation(isSingleRowMode: boolean): number {
    // 从单列切换到双列时，extraScrollOffset 为 14
    // 从双列切换到单列时，extraScrollOffset 为 0
    const extraOffset = isSingleRowMode === false ? 14 : 0;
    return this.getListOptionsHeight() + extraOffset;
  }

  /**
   * 获取当前视口中第一个可见的商品元素（兼容旧代码的静态方法）
   * @deprecated 请使用实例方法 getFirstVisibleProductElement()
   */
  static getFirstVisibleProductElement(
    options: {
      selector?: string;
      visibilityThreshold?: number;
    } = {},
  ): HTMLElement | null {
    const manager = new ProductListScrollManager({
      productSelector: options.selector,
      visibilityThreshold: options.visibilityThreshold,
    });
    return manager.getFirstVisibleProductElement();
  }

  /**
   * 计算页面滚动偏移位置（兼容旧代码的静态方法）
   * @deprecated 请使用实例方法 getScrollOffset()
   */
  static getScrollOffset(
    options: {
      listOptionsSelector?: string;
      headerSelector?: string;
    } = {},
  ): number {
    const manager = new ProductListScrollManager({
      listOptionsSelector: options.listOptionsSelector,
      headerSelector: options.headerSelector,
    });
    return manager.getScrollOffset();
  }
}

/** 商品卡片标签项 */
export interface ProductCardTagItem {
  content?: string;
  fill?: string;
  has_content?: number;
  has_fill?: number;
  has_stroke?: number;
  stroke?: string;
  color?: string;
}

/** 商品卡片标签返回值 */
export interface GetProductCardTagsResult {
  tagList: ProductCardTagItem[];
  tagsForSensors: string[];
}

/** 商品图片返回值 */
export interface GetProductImageResult {
  mainImage: { src: string; isGray: boolean };
  hoverImage: { src: string; isGray: boolean };
  hasGrayImage: boolean;
}
/** getProductImage 入参商品（列表项 / 关联项 + is_glass） */
export type GetProductImageInput =
  | ((ProductsListCorrelationItem | ProductsListItem) & { is_glass?: number })
  | null
  | undefined;

/**
 * 获取商品主图/悬浮图及是否灰底图
 * @param params.product - 商品对象（含图片键）
 * @param params.showMaterialImg - 是否优先材质图
 * @param params.image_key - 图片键名，默认 'origin_image'
 * @returns mainImage、hoverImage、hasGrayImage
 */
export function getProductImage({
  product,
  showMaterialImg = false,
  image_key = 'origin_image',
}: {
  product: GetProductImageInput;
  showMaterialImg?: boolean;
  image_key?: keyof ProductsListCorrelationItem | 'image';
}): GetProductImageResult {
  const imgSet = product?.[image_key as keyof NonNullable<GetProductImageInput>] as
    | ProductsListCorrelationImage
    | undefined;
  const hasGrayImage = Boolean(imgSet?.product_gray && imgSet.product_gray.length > 0);
  /** 镜框且灰底≥2：默认侧向灰底 main=gray[1]，hover 切正面 gray[0]（VLAM-21529） */
  const useGlassSideMain = product?.is_glass === 1 && (imgSet?.product_gray?.length ?? 0) >= 2;
  /** 无灰底、仅白底且镜框、白底≥2：主 product[1]、hover product[0]（VLAM-21529 补充） */
  const useGlassWhiteSecondMain =
    !hasGrayImage && product?.is_glass === 1 && (imgSet?.product?.length ?? 0) >= 2;

  const getMainImage = (): { src: string; isGray: boolean } => {
    try {
      if (!product || !imgSet) {
        return { src: '', isGray: false };
      }
      if (showMaterialImg && (imgSet.material?.length ?? 0) > 0) {
        return { src: imgSet.material![0], isGray: false };
      }
      if (hasGrayImage) {
        if (useGlassSideMain) {
          return { src: imgSet.product_gray![1], isGray: true };
        }
        return { src: imgSet.product_gray![0], isGray: true };
      }
      if (useGlassWhiteSecondMain) {
        return { src: imgSet.product![1], isGray: false };
      }
      return {
        src: imgSet.product?.[0] ?? '',
        isGray: false,
      };
    } catch (error) {
      console.error('Failed to get main image:', error);
      return { src: '', isGray: false };
    }
  };

  const getHoverImage = (): { src: string; isGray: boolean } => {
    try {
      if (!product || !imgSet) {
        return { src: '', isGray: false };
      }
      if (showMaterialImg && (imgSet.material?.length ?? 0) > 1) {
        return { src: imgSet.material![1], isGray: false };
      }
      if (hasGrayImage && (imgSet.product_gray?.length ?? 0) > 1) {
        if (useGlassSideMain) {
          return { src: imgSet.product_gray![0], isGray: true };
        }
        return { src: imgSet.product_gray![1], isGray: true };
      }
      if (useGlassWhiteSecondMain) {
        return { src: imgSet.product![0], isGray: false };
      }
      return {
        src: imgSet.product?.[1] ?? '',
        isGray: false,
      };
    } catch (error) {
      console.error('Failed to get hover image:', error);
      return { src: '', isGray: false };
    }
  };

  return {
    mainImage: getMainImage(),
    hoverImage: getHoverImage(),
    hasGrayImage,
  };
}
// 判断是否是平板设备（iPad或Android平板）
export function isTablet(): boolean {
  const userAgent = navigator.userAgent;
  // 判断是否是平板设备（iPad或Android平板）
  const isIPad =
    /iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
  const isAndroidTablet = /Android/i.test(userAgent);
  return isIPad || isAndroidTablet;
}
/** 同盾设备指纹写入 localStorage 的 key */
export const TONG_DUN_BLACK_BOX_KEY = 'vo_tong_dun_black_box';

let tongDunEnsurePromise: Promise<void> | null = null;
function applyTongDunFmOpt(publicConfig: Record<string, unknown>): void {
  const w = window as Window & {
    _fmOpt?: { partner: string; success: (data: string) => void };
  };
  const partner =
    typeof publicConfig.VUE_APP_TONG_DUN_PARTNER_ID === 'string'
      ? publicConfig.VUE_APP_TONG_DUN_PARTNER_ID
      : 'marvel';
  w._fmOpt = {
    partner,
    success(data: string) {
      try {
        // console.log('set black box TONG_DUN_BLACK_BOX_KEY', data);
        localStorage.setItem(TONG_DUN_BLACK_BOX_KEY, data);
      } catch (e) {
        console.warn('[TongDun] localStorage set failed', e);
      }
    },
  };
}

/**
 * 同盾 TrustDecision（fm.js）全站单例加载；登录/注册弹窗、支付、购物车/商详等可重复调用。
 */
export function ensureTongDunScript(publicConfig: Record<string, unknown>): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  applyTongDunFmOpt(publicConfig);
  const src = publicConfig.VUE_APP_TONG_DUN_BASE_URL;
  if (typeof src !== 'string' || !src) {
    return Promise.resolve();
  }
  if (tongDunEnsurePromise) {
    return tongDunEnsurePromise;
  }
  tongDunEnsurePromise = loadScript(src, {
    defer: false,
    async: false,
  }).catch((err: unknown) => {
    tongDunEnsurePromise = null;
    return Promise.reject(err);
  });
  return tongDunEnsurePromise;
}

type AppleDeviceType = 'iPhone' | 'iPad' | 'iPod' | 'Mac' | 'Unknown';

/**
 * 获取当前苹果设备类型
 */
export function getAppleDeviceType(): AppleDeviceType {
  const ua: string = navigator.userAgent;

  // iOS 设备判断（iPhone / iPad / iPod）
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/iPod/.test(ua)) return 'iPod';

  // macOS 判断
  if (/Macintosh|Mac OS X/.test(ua)) {
    // iPadOS 13+ Safari 会将平台识别为 Mac
    // 如果是触控屏设备且平台是 Mac，可认为是 iPad
    const isTouchDevice: boolean = navigator.maxTouchPoints > 0;
    if (isTouchDevice) return 'iPad';
    return 'Mac';
  }

  return 'Unknown';
}

/**
 * 判断是否为苹果设备
 */
export function isAppleDevice(): boolean {
  return getAppleDeviceType() !== 'Unknown';
}

/** {@link getGiftListProductMainImage} 返回值 */
export type GiftListProductMainImage = { src: string; isGray: boolean };

type GiftListProductImageInput =
  | {
      img?: string | null;
      img_gray?: string | null;
      img_celebrity?: string | null;
      img_material?: string | null;
    }
  | null
  | undefined;

/**
 * 赠品列表商品主图：`gift_list[].product` 使用 `img` / `img_gray` 等字段。
 * 有灰底优先灰底；否则白底 `img`；再兜底模特图等。
 */
export function getGiftListProductMainImage(
  product: GiftListProductImageInput,
): GiftListProductMainImage {
  if (!product) {
    return { src: '', isGray: false };
  }

  const pick = (v: unknown): string => {
    if (typeof v !== 'string') return '';
    return v.trim();
  };

  const gray = pick(product.img_gray);
  const white = pick(product.img);
  const celebrity = pick(product.img_celebrity);
  const material = pick(product.img_material);

  if (gray) return { src: gray, isGray: true };
  if (white) return { src: white, isGray: false };
  if (material) return { src: material, isGray: false };
  if (celebrity) return { src: celebrity, isGray: false };

  return { src: '', isGray: false };
}
